import { OverviewIcon } from "@minket/brand/icons/overview";
import { type alertList, limits } from "@minket/core";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { type Alert, AlertLedger } from "@/components/ops/alerts";
import { count, plural } from "@/components/ops/format";
import { ReputationScale } from "@/components/ops/scale";
import { Choice } from "@/components/ops/select";
import { DayTable, Figures, fillDays, VolumeChart, VolumeGhost } from "@/components/ops/volume";
import { Page } from "@/components/page/page";
import { OrgScope } from "@/components/page/scope";
import { Section, Sheet } from "@/components/page/section";
import { Notice, QueryNotice } from "@/components/page/states";
import { endpoints, type Wire } from "@/lib/endpoints";
import type { Organization } from "@/lib/queries";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/")({
  component: Overview,
});

type Range = "7" | "30" | "90";
type Stream = "all" | "transactional" | "marketing";
type AlertPage = Wire<typeof alertList>;

/** Whole UTC days ending tomorrow, so today is in and the query key holds for the day. */
function dayRange(days: number) {
  const to = new Date();
  to.setUTCHours(0, 0, 0, 0);
  to.setUTCDate(to.getUTCDate() + 1);
  const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
  return { from_date: from.toISOString(), to_date: to.toISOString(), interval: "day" };
}

/** Open alerts the overview lists before it sends people to the alerts page. */
const SHOWN = 6;

/**
 * What happened and whether anything is wrong (direction §7): the sentence under the title is
 * the verdict, the engraved plate below it the period's mail, then the reputation scales and the
 * alerts still open. Setup lives in the setup tile alone (the owner's rule), never here.
 */
function Overview() {
  const { organization, project } = useWorkspace();
  const orgId = organization?.organization_id ?? "";
  const [range, setRange] = useState<Range>("30");
  const [scope, setScope] = useState<"org" | "project">("org");
  const [stream, setStream] = useState<Stream>("all");
  const window = dayRange(Number(range));
  const metrics = useQuery({
    ...apiQuery(
      endpoints.metrics,
      { orgId },
      {
        ...window,
        ...(scope === "project" && project ? { project_id: project.id } : {}),
        ...(stream === "all" ? {} : { stream }),
      },
    ),
    enabled: orgId !== "",
  });
  const alerts = useQuery({
    ...apiQuery(endpoints.alerts, { orgId }, { status: "active", limit: 25 }),
    enabled: orgId !== "",
  });
  const details = useQuery({
    ...apiQuery(endpoints.organization, { orgId }),
    enabled: orgId !== "",
  });
  const days = metrics.data
    ? fillDays(metrics.data.points, window.from_date, window.to_date)
    : null;

  return (
    <Page title="Overview" trace={days?.map((day) => day.sent)}>
      {organization ? (
        // The filters are this tab's, not the section's, so they stay under the tabs beside the
        // verdict rather than move into the title row with a page's action.
        <div className="flex min-w-0 flex-wrap items-center justify-between gap-x-8 gap-y-3">
          <p className="max-w-[72ch] text-sm text-muted-foreground wrap-break-word">
            <Verdict alerts={alerts} />
          </p>
          <div className="ml-auto flex min-w-0 flex-wrap items-end gap-2.5">
            <Choice
              label="Period"
              value={range}
              onChange={setRange}
              options={[
                ["7", "Last 7 days"],
                ["30", "Last 30 days"],
                ["90", "Last 90 days"],
              ]}
            />
            <Choice
              label="Projects"
              value={scope}
              onChange={setScope}
              options={[
                ["org", "Every project"],
                ...(project ? ([["project", project.name]] as const) : []),
              ]}
            />
            <Choice
              label="Stream"
              value={stream}
              onChange={setStream}
              options={[
                ["all", "Both streams"],
                ["transactional", "Transactional"],
                ["marketing", "Marketing"],
              ]}
            />
          </div>
        </div>
      ) : null}
      <OrgScope
        will="Its mail, its reputation and its open alerts appear here once it sends."
        icon={<OverviewIcon />}
        preview={() => <VolumeGhost />}
      >
        {() => (
          <Sheet>
            <SendingQuota organization={details.data} />
            <Section
              wide
              title="Live mail"
              description="Opens include Apple Mail Privacy Protection, which loads every image as mail arrives; clicks are the firmer signal."
            >
              <QueryNotice query={metrics} noun="delivery numbers">
                {(data) =>
                  days && data.totals.sent > 0 ? (
                    <>
                      <Figures metrics={data} days={days.length} />
                      <VolumeChart days={days} reputation={data.reputation} />
                      <DayTable points={data.points} />
                    </>
                  ) : (
                    <>
                      <Figures metrics={data} days={days?.length ?? 1} />
                      <Notice>No live mail in these {range} days.</Notice>
                    </>
                  )
                }
              </QueryNotice>
              {metrics.isSuccess ? null : <VolumeGhost />}
            </Section>
            <Section
              title="Reputation"
              description={`Over the period's live sends. The kill switch reads each project's last ${limits.killSwitch.windowHours} hours instead, once ${count(limits.killSwitch.minimumSample)} sends are in them.`}
            >
              <QueryNotice query={metrics} noun="reputation">
                {(data) => (
                  <div className="grid min-w-0 gap-x-10 gap-y-8 @min-[44rem]:grid-cols-2">
                    <ReputationScale
                      label="Bounce rate"
                      gauge={data.reputation.bounce}
                      minimum={data.reputation.minimum_sample}
                      killSwitch={limits.killSwitch.bounceRate}
                    />
                    <ReputationScale
                      label="Complaint rate"
                      gauge={data.reputation.complaint}
                      minimum={data.reputation.minimum_sample}
                      killSwitch={limits.killSwitch.complaintRate}
                    />
                  </div>
                )}
              </QueryNotice>
            </Section>
            <OpenAlerts query={alerts} />
          </Sheet>
        )}
      </OrgScope>
    </Page>
  );
}

/**
 * A sandbox organization's quota (docs/design/signup.md): live mail counted today and this
 * month against the limits, as plain figures, with how to change them.
 */
function SendingQuota({ organization }: { organization: Organization | undefined }) {
  if (!organization) return null;
  const daily = organization.daily_quota;
  const monthly = organization.monthly_quota;
  if (daily === null && monthly === null) return null;
  return (
    <Section wide title="Sending quota">
      <dl className="grid gap-4 text-sm @min-[34rem]:grid-cols-2">
        {daily === null ? null : (
          <div className="grid gap-0.5">
            <dt className="text-muted-foreground">Today</dt>
            <dd className="type-data text-[15px] whitespace-nowrap">
              {organization.daily_used} of {daily}
            </dd>
          </div>
        )}
        {monthly === null ? null : (
          <div className="grid gap-0.5">
            <dt className="text-muted-foreground">This month</dt>
            <dd className="type-data text-[15px] whitespace-nowrap">
              {organization.monthly_used} of {monthly}
            </dd>
          </div>
        )}
      </dl>
      <p className="text-sm text-muted-foreground">Minket raises quotas on request.</p>
    </Section>
  );
}

/** The sentence under the title: whether anything is wrong, with the count. */
function Verdict({ alerts }: { alerts: UseQueryResult<AlertPage> }) {
  if (!alerts.data) return null;
  const open = alerts.data.data;
  if (open.length === 0) return <>No alert is open.</>;
  const critical = open.filter((alert) => alert.severity === "critical").length;
  const counted = alerts.data.has_more
    ? `${count(open.length)} or more alerts are open`
    : `${plural(open.length, "alert")} ${open.length === 1 ? "is" : "are"} open`;
  const severity =
    critical === 0
      ? ""
      : open.length === 1
        ? ", and it is critical"
        : `, ${count(critical)} critical`;
  return (
    <>
      {counted}
      {severity}.
    </>
  );
}

function OpenAlerts({ query }: { query: UseQueryResult<AlertPage> }) {
  const open: readonly Alert[] = query.data?.data ?? [];
  if (query.isSuccess && open.length === 0) return null;
  return (
    <Section wide title="Open alerts">
      <QueryNotice query={query} noun="open alerts">
        {(data) => (
          <>
            <AlertLedger alerts={data.data.slice(0, SHOWN)} label="Open alerts" />
            {data.data.length > SHOWN || data.has_more ? (
              <Link
                to="/alerts"
                className="w-fit rounded-sm text-sm font-medium text-primary underline decoration-1 underline-offset-4 hover:decoration-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
              >
                {data.has_more
                  ? "Every open alert"
                  : `${plural(data.data.length - SHOWN, "more open alert")}`}
              </Link>
            ) : null}
          </>
        )}
      </QueryNotice>
    </Section>
  );
}
